'use client'

import { Button } from '@vinted/web-ui'
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  getSnadComplaint,
  prepareReturnLabelOrder,
  transformComplaintDto,
  ComplaintModel,
  ReturnShippingModal,
  transformReturnShipmentOrderDto,
  clickEvent,
} from '@marketplace-web/complaints/complaints-data'

import GenericErrorModal from '../GenericErrorModal'
import RequestReturnModal from '../RequestReturnModal'
import ReturnShipmentCheckout from '../ReturnShipmentCheckout'
import { RequestReturnDetailsContext } from './RequestReturnProvider'

export interface RequestReturnRef {
  handleReturnModalOpen: () => void
}

type Props = Partial<ConversationActionProps> & {
  isChooseResolutionFlow?: boolean
  complaint?: ComplaintModel | null
  url?: string
  text?: string
  conversationTransactionId?: number
  conversationTransactionIsBundle?: boolean
}

const RequestReturn = forwardRef(
  (
    {
      primary,
      title,
      inverse,
      theme,
      size,
      complaint: preFetchedComplaint,
      url,
      text,
      isChooseResolutionFlow,
      conversationTransactionId,
      conversationTransactionIsBundle,
    }: Props,
    ref,
  ) => {
    const { track } = useTracking()

    const [activeDialog, setActiveDialog] = useState<ReturnShippingModal | null>(
      ReturnShippingModal.None,
    )

    const buttonTitle = title || text

    const {
      fetch: fetchSnadComplaint,
      transformedData: fetchedComplaint,
      isLoading: isComplaintLoading,
    } = useFetch(getSnadComplaint, transformComplaintDto)

    const { fetch: fetchPrepareReturnLabelOrder, transformedData: returnShipmentOrder } = useFetch(
      prepareReturnLabelOrder,
      response => transformReturnShipmentOrderDto(response.return_label_order),
    )

    const complaint = preFetchedComplaint || fetchedComplaint
    const transactionId = conversationTransactionId || complaint?.transactionId

    const {
      fetchRequestReturnDetails,
      updateSelectedPaymentSide,
      uiState,
      requestReturnDetailsError,
      setRequestReturnDetailsError,
    } = useContext(RequestReturnDetailsContext)

    const openErrorDialog = () => setActiveDialog(ReturnShippingModal.GenericErrorModal)

    useEffect(() => {
      if (!requestReturnDetailsError) return

      openErrorDialog()
    })

    const fetchReturnShipment = () => {
      if (!transactionId) return

      fetchRequestReturnDetails({ transactionId })
    }

    const fetchComplaint = async () => {
      if (preFetchedComplaint || !transactionId) return

      const response = await fetchSnadComplaint(transactionId)

      if (response.error) {
        openErrorDialog()
      }
    }

    const trackRequestReturnModalOpen = () => {
      if (!transactionId) return

      track(
        clickEvent({
          screen: 'complaint_proof',
          target: 'confirm_submit_request_return',
          targetDetails: JSON.stringify({
            transaction_id: transactionId,
          }),
        }),
      )
    }

    const handleReturnModalOpen = () => {
      fetchComplaint()
      fetchReturnShipment()
      trackRequestReturnModalOpen()
      setActiveDialog(ReturnShippingModal.RequestReturnModal)
    }

    useImperativeHandle(ref, () => ({
      handleReturnModalOpen,
    }))

    const handleReturnModalClose = () => {
      setActiveDialog(ReturnShippingModal.None)
      updateSelectedPaymentSide(null)
      setRequestReturnDetailsError(undefined)
    }

    const prepareReturnLabelOrderForCheckout = () => {
      if (!transactionId) return

      fetchPrepareReturnLabelOrder({
        transactionId: transactionId.toString(),
      })

      setActiveDialog(ReturnShippingModal.Checkout)
    }

    const handleProceedToCheckout = () => {
      fetchReturnShipment()
      if (transactionId) fetchRequestReturnDetails({ transactionId })
      prepareReturnLabelOrderForCheckout()
    }

    const renderActiveModal = () => {
      switch (activeDialog) {
        case ReturnShippingModal.RequestReturnModal:
          return (
            <RequestReturnModal
              transactionId={transactionId}
              url={url}
              onClose={handleReturnModalClose}
              onProceedToCheckout={handleProceedToCheckout}
              isDataLoading={uiState === UiState.Pending || isComplaintLoading}
              onFetchReturnShipment={fetchReturnShipment}
              complaint={complaint}
              conversationTransactionIsBundle={conversationTransactionIsBundle}
            />
          )
        case ReturnShippingModal.Checkout:
          return (
            <ReturnShipmentCheckout
              returnShipmentOrder={returnShipmentOrder}
              onErrorDialogOpen={openErrorDialog}
            />
          )
        case ReturnShippingModal.GenericErrorModal:
          return <GenericErrorModal isShown onClose={handleReturnModalClose} />
        default:
          return null
      }
    }

    if (!transactionId) return null

    return (
      <>
        {!isChooseResolutionFlow && (
          <ConversationActionButton
            text={buttonTitle}
            primary={primary}
            size={size || Button.Size.Medium}
            theme={theme}
            inverse={inverse}
            onClick={handleReturnModalOpen}
            testId="request-return-action-button"
          />
        )}
        {renderActiveModal()}
      </>
    )
  },
)

export default RequestReturn
